import { useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Music, FileText, X, ExternalLink } from "lucide-react";

export const mediaUrl = (m) => {
  let t = "";
  try { t = localStorage.getItem("icdh_token") || ""; } catch (e) { /* noop */ }
  return `${API}/files/${m.path}${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
};

const KINDS = [
  { kind: "image", label: "Ajouter une image", accept: ".jpg,.jpeg,.png,image/jpeg,image/png", re: /\.(jpe?g|png)$/i, err: "JPG ou PNG uniquement.", Icon: ImageIcon },
  { kind: "audio", label: "Ajouter un audio", accept: ".mp3,audio/mpeg", re: /\.mp3$/i, err: "MP3 uniquement.", Icon: Music },
  { kind: "pdf", label: "Ajouter un PDF", accept: ".pdf,application/pdf", re: /\.pdf$/i, err: "PDF uniquement.", Icon: FileText },
];

// Editor: one image, one audio and one PDF per question.
export function QuestionMediaEditor({ media, onChange, idx }) {
  const [busy, setBusy] = useState("");
  const m = media || {};
  const upload = async (k, file) => {
    if (!k.re.test(file.name)) { toast.error(`Format non autorisé : ${k.err}`); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Fichier trop volumineux (15 Mo maximum)."); return; }
    setBusy(k.kind);
    try {
      const fd = new FormData(); fd.append("kind", k.kind); fd.append("file", file);
      const r = await api.raw.post("/exams/question-media", fd);
      onChange({ ...m, [k.kind]: r.data });
      toast.success("Fichier ajouté");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const remove = (kind) => { const n = { ...m }; delete n[kind]; onChange(Object.keys(n).length ? n : null); };
  return (
    <div className="mb-2 space-y-2" data-testid={`q-media-${idx}`}>
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <label key={k.kind} className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1 text-xs text-primary cursor-pointer hover:bg-muted" data-testid={`q-media-${k.kind}-btn-${idx}`}>
            <k.Icon className="h-3.5 w-3.5" />{busy === k.kind ? "Envoi…" : (m[k.kind] ? k.label.replace("Ajouter", "Remplacer") : k.label)}
            <input type="file" className="hidden" accept={k.accept} disabled={!!busy} data-testid={`q-media-${k.kind}-input-${idx}`}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(k, f); }} />
          </label>
        ))}
      </div>
      {KINDS.filter((k) => m[k.kind]).map((k) => (
        <div key={k.kind} className="flex items-center gap-2 text-xs" data-testid={`q-media-${k.kind}-item-${idx}`}>
          {k.kind === "image" ? <img src={mediaUrl(m.image)} alt="" className="h-12 w-12 rounded object-cover border border-border" /> : <k.Icon className="h-4 w-4 text-muted-foreground" />}
          <span className="truncate max-w-[220px]">{m[k.kind].filename}</span>
          <button type="button" className="text-destructive" onClick={() => remove(k.kind)} aria-label="Retirer" data-testid={`q-media-${k.kind}-remove-${idx}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

// Student / preview display.
export function QuestionMediaView({ media, idx }) {
  if (!media) return null;
  return (
    <div className="mt-3 space-y-3" data-testid={`question-media-${idx}`}>
      {media.image && <img src={mediaUrl(media.image)} alt="Illustration de la question" className="max-h-80 max-w-full rounded-md border border-border" data-testid={`question-image-${idx}`} />}
      {media.audio && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Écoutez l'audio avant de répondre</p>
          <audio controls preload="metadata" src={mediaUrl(media.audio)} className="w-full max-w-md" data-testid={`question-audio-${idx}`}>Votre navigateur ne lit pas l'audio.</audio>
        </div>
      )}
      {media.pdf && (
        <div className="space-y-2">
          <iframe title={`Document question ${idx + 1}`} src={mediaUrl(media.pdf)} className="w-full h-96 rounded-md border border-border" data-testid={`question-pdf-viewer-${idx}`} />
          <a href={mediaUrl(media.pdf)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary underline" data-testid={`question-pdf-link-${idx}`}>
            <ExternalLink className="h-4 w-4" />Ouvrir le document ({media.pdf.filename})
          </a>
        </div>
      )}
    </div>
  );
}
